import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './About.css';

const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-in');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    // Observe all sections for animation
    const sections = document.querySelectorAll('.animate-section, .feature-card, .brand-card, .testimonial-card, .stat-item');
    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <div className="about-page">
      {/* Hero Section */}
<section className="about-hero">
  <div className="container">
    <div className="hero-content">
      <h1>Our Journey in Solar Excellence</h1>
      <p className="hero-subtitle">
        For over a decade, KingSolarman has been at the forefront of Botswana's solar revolution. 
        Our mission is to provide sustainable energy solutions that empower communities, 
        reduce carbon footprints, and drive economic growth across the nation.
      </p>
      
      <div className="about-hero-stats">
        <div className="stat-item-1">
          <span className="stat-number">2010</span>
          <span className="stat-label">Founded</span>
        </div>
        <div className="stat-item-1">
          <span className="stat-number">50+</span>
          <span className="stat-label">Expert Team</span>
        </div>
        <div className="stat-item-1">
          <span className="stat-number">5M+</span>
          <span className="stat-label">KWh Generated</span>
        </div>
        <div className="stat-item-1">
          <span className="stat-number">15+</span>
          <span className="stat-label">Regions Served</span>
        </div>
      </div>
    </div>
  </div>
</section>

      <div className="container">
        {/* Mission & Vision */}
        <section className="mission-vision animate-section">
          <div className="mission">
            <h2>Our Mission</h2>
            <p>
              To empower every Motswana household and business with affordable, 
              reliable solar energy solutions that reduce electricity costs, 
              promote energy independence, and contribute to Botswana's sustainable development.
            </p>
          </div>
          <div className="vision">
            <h2>Our Vision</h2>
            <p>
              To be Botswana's leading solar energy provider, recognized for 
              excellence in product quality, customer service, and environmental 
              stewardship by 2025.
            </p>
          </div>
        </section>

        {/* Our Story */}
        <section className="our-story animate-section">
          <h2>Our Story</h2>
          <div className="story-content">
            <div className="story-text">
              <p>
                Founded in Gaborone's International Commerce Park, KingSolarman 
                was born from a vision to bring reliable, sustainable energy solutions 
                to Botswana. Recognizing the country's abundant sunshine and growing 
                energy needs, we set out to become a leader in solar technology.
              </p>
              <p>
                Starting with solar installations for local businesses, we quickly 
                expanded to serve residential customers, government institutions, 
                and large-scale industrial projects across Botswana. Our location 
                in International Commerce Park positions us at the heart of Botswana's 
                commercial activity, allowing us to serve customers efficiently 
                nationwide.
              </p>
              <p>
                Today, KingSolarman is proud to contribute to Botswana's energy 
                diversification goals while providing our customers with cutting-edge 
                solar solutions that withstand local conditions and deliver 
                outstanding performance year after year.
              </p>
            </div>
            <div className="story-stats">
              <div className="stat-item">
                <div className="stat-number">1000+</div>
                <div className="stat-label">Solar Installations</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">500+</div>
                <div className="stat-label">Business Clients</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">24</div>
                <div className="stat-label">Districts Served</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">98%</div>
                <div className="stat-label">Customer Satisfaction</div>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="why-choose-us animate-section">
          <h2>Why Choose KingSolarman</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 15l8.5-8.5C21.92 5.92 23 7 23 9.5V16c0 1.5-1 2.5-2.5 2.5H15c-2.5 0-3.58-1.08-5.5-3l-2.5-2.5"/>
                  <path d="M14 10l-3.5 3.5L9 12.5"/>
                  <path d="M8 16l-3.5-3.5C3.08 11.92 2 10.5 2 8V4c0-1.5 1-2.5 2.5-2.5H8c2.5 0 3.58 1.08 5.5 3l2.5 2.5"/>
                </svg>
              </div>
              <h3>Local Expertise</h3>
              <p>
                Based in Botswana, we understand local climate conditions, regulations, 
                and energy needs better than international suppliers.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                </svg>
              </div>
              <h3>Quality Products</h3>
              <p>
                We import directly from world-leading manufacturers, ensuring 
                genuine products with valid warranties for Botswana's market.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 8v4l3 3"/>
                </svg>
              </div>
              <h3>Quick Installation</h3>
              <p>
                Our certified teams provide fast, professional installation with 
                minimal disruption to your home or business.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
              </div>
              <h3>After-Sales Support</h3>
              <p>
                Comprehensive warranty support, maintenance services, and 
                24/7 technical assistance from our Gaborone headquarters.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
              <h3>Custom Solutions</h3>
              <p>
                We design systems specifically for Botswana's environment, 
                considering dust, heat, and seasonal variations.
              </p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                </svg>
              </div>
              <h3>Competitive Pricing</h3>
              <p>
                As direct importers, we eliminate middlemen to offer the best 
                prices for quality solar equipment in Botswana.
              </p>
            </div>
          </div>
        </section>



        {/* Testimonials */}
        <section className="testimonials animate-section">
          <h2>What Our Botswana Customers Say</h2>
          <div className="testimonials-grid">
            <div className="testimonial-card">
              <div className="testimonial-rating">★★★★★</div>
              <div className="testimonial-content">
                <p>
                  "KingSolarman installed solar at our farm in Ghanzi and we've been 
                  completely off-grid for 2 years. The system handles our irrigation 
                  pumps perfectly."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-name">Mr. Kgosi</div>
                <div className="author-location">Ghanzi District</div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">★★★★★</div>
              <div className="testimonial-content">
                <p>
                  "Our hotel in Maun needed reliable power. KingSolarman designed 
                  a system that saves us 70% on electricity costs. Professional 
                  service from start to finish."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-name">Hotel Manager</div>
                <div className="author-location">Maun</div>
              </div>
            </div>
            <div className="testimonial-card">
              <div className="testimonial-rating">★★★★★</div>
              <div className="testimonial-content">
                <p>
                  "From Gaborone to Francistown, their team provided excellent 
                  service. Our 10kW system powers our entire manufacturing facility."
                </p>
              </div>
              <div className="testimonial-author">
                <div className="author-name">Factory Owner</div>
                <div className="author-location">Francistown</div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action */}
        <section className="about-cta animate-section">
          <div className="cta-content">
            <h2>Ready to Harness Botswana's Sunshine?</h2>
            <p>
              Visit our showroom in International Commerce Park, Gaborone or 
              contact us for a free solar consultation.
            </p>
            <div className="contact-info">
              <div className="contact-item">
                <span className="contact-icon">📍</span>
                <span>International Commerce Park, Gaborone, Botswana</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">📞</span>
                <span>+267 XXX-XXX-XXX</span>
              </div>
              <div className="contact-item">
                <span className="contact-icon">✉️</span>
                <span>info@kingsolarman.co.bw</span>
              </div>
            </div>
            <div className="cta-buttons">
              <Link to="/products" className="cta-btn primary">Shop Solar Products</Link>
              <Link to="/contact" className="cta-btn secondary">Schedule Consultation</Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;